import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[rgba(9,10,9,0.7)] backdrop-blur-xl border-b border-[var(--mpg-border)]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1800px] mx-auto px-8 md:px-16 py-3 md:py-3.5 flex items-center justify-between">
        <a href="#top" className="flex items-center shrink-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Madison Price Group" className="brand-logo object-contain" />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--mpg-text-soft)] hover:text-[var(--mpg-text)] transition-colors duration-300 link-underline"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          className="md:hidden text-[var(--mpg-text)]"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden bg-[rgba(9,10,9,0.95)] backdrop-blur-xl border-b border-[var(--mpg-border)]"
            data-testid="mobile-menu"
          >
            <div className="px-8 py-8 flex flex-col gap-6">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-3xl text-[var(--mpg-text)]"
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
